/**
 * Форма ручного ввода паспорта — `/api/supervisor/passport-form`.
 *
 * Состав формы приходит с сервера: он собирается из реестра показателей, того
 * же, что питает Excel-шаблон. Клиент не хранит собственного каталога — иначе
 * подписи, единицы и адреса записи неизбежно разъезжаются с серверными.
 *
 * Здесь только типы контракта и тонкие обёртки над axios; вся логика раскладки
 * и валидации — на странице и на сервере соответственно.
 */
import { api } from './api';

// ------------------------------------------------------------------ контракт

/** Тип значения показателя — определяет контрол ввода и приведение при отправке. */
export type Dtype = 'int' | 'float' | 'percent' | 'str' | 'date' | 'bool';

/**
 * Слой, из которого ВЗЯТО значение (US-08 FR-5, US-39 FR-8): `bns` ·
 * `snp_monitoring` · `treasury` · `manual` · `none` («нет данных»). Выбран
 * источник, а данных у него нет, — тоже `none` (US-39 FR-11.1). Подписи
 * локализует бэкенд: экран показывает их как есть.
 */
export interface PassportSource {
  layer: string;
  code?: string | null;
  label_ru: string;
  label_kz: string;
}

/** Вариант тумблера источника (US-39 FR-10.1) — значение для `setSourceMode`. */
export interface SourceOption {
  value: string;
  label_ru: string;
  label_kz: string;
}

/**
 * Выбор источника и блокировка поля (US-39, DP-545). Общие у показателя и
 * блока: у таблицы выбор и замок — на неё целиком (FR-11.2, FR-15).
 *
 * Поля необязательные, потому что фронт обязан пережить бэкенд до US-39: там
 * их нет, и работает прежний двоичный тумблер по `snp_source` / `manual_mode`
 * (см. `sourceStateOf`). Удалить фолбэк, когда US-39 раскатан во всех регионах.
 */
export interface SourceChoice {
  /** Только объявленные у показателя источники + «Ручной ввод»; `[]` — тумблера нет. */
  source_options?: SourceOption[];
  /** Действующий выбор: `bns` · `snp_monitoring` · `treasury` · `manual` · `null` (источника нет). */
  source_mode?: string | null;
  /** `false` — действует умолчание (FR-11a), оператор не выбирал. */
  source_mode_explicit?: boolean;
  /** Поле только для чтения: выбран источник, а не ручной ввод (FR-11). */
  locked?: boolean;
  /** Бейдж: слой, из которого взято значение. */
  source?: PassportSource | null;
  /** Период данных ручного ввода и автор последней правки (FR-8.1). */
  period_from?: string | null;
  period_to?: string | null;
  /** Keycloak `sub` автора, а не имя. */
  updated_by?: string | null;
}

export interface PassportField extends SourceChoice {
  key: string;
  label_ru: string;
  label_kz: string;
  unit: string | null;
  dtype: Dtype;
  /** Величина ВЫБРАННОГО слоя (US-39 FR-4). */
  value: unknown;
  /**
   * Что ввёл оператор — видно и тогда, когда показатель на источнике: введённое
   * не теряется при переключении (FR-13).
   */
  manual_value?: unknown;
  /** Обязательное поле: очищенное сервер не сохранит (US-08 FR-4.9). */
  required?: boolean;
  /** Заполнен — показатель обычно приходит из «Мониторинга СНП» (до US-39). */
  snp_source: string | null;
  /**
   * Оператор перевёл показатель на ручной ввод — по ЭТОЙ территории и году.
   * Хранится на сервере (`source_mode`), меняется `setSourceMode`.
   */
  manual_mode: boolean;
  /**
   * Значение — id блоба в хранилище, а не текст: такое поле вводится файлом,
   * и интерфейс рисует для него карточку с фото вместо поля ввода. Признак из
   * реестра показателей — раньше это же знание фронт и бэкенд опознавали
   * сравнением ключа с `general.akim_image_id`, каждый у себя (DP-461).
   */
  is_image?: boolean;
  /**
   * Ссылка на блоб у полей-изображений (фото акима). Приходит с сервера, и
   * собрать её на клиенте нельзя: под united-бэкендом в URL нужен код региона,
   * а фронт его не знает. `null`/отсутствует — значения нет либо хранилище
   * ссылку не даёт (mock); тогда остаётся `akimPhotoUrl`.
   */
  image_url?: string | null;
}

export interface PassportColumn {
  column: string;
  label_ru: string;
  label_kz: string;
  unit: string | null;
  dtype: Dtype;
  /** Колонка задаёт личность строки (продукт, отрасль) — показываем текстом. */
  readonly: boolean;
}

export interface PassportBlock extends SourceChoice {
  id: string;
  table: string;
  label_ru: string;
  label_kz: string;
  /** Заполнены у подблоков, различающихся типом строки (СЭР / Продовольствие). */
  discriminator_column: string | null;
  discriminator_value: string | null;
  columns: PassportColumn[];
  rows: Record<string, unknown>[];
  /** Строки оператора, пока блок на источнике (FR-13); `null` — их нет. */
  manual_rows?: Record<string, unknown>[] | null;
  snp_source: boolean;
  /** Выбор источника по блоку — как у показателя (см. `PassportField`). */
  manual_mode: boolean;
  /**
   * Набор строк канонический (справочник продуктов): строки не добавляются и не
   * удаляются, колонки с `readonly` заполнены сервером.
   */
  fixed_rows: boolean;
}

export interface PassportSection {
  title_ru: string;
  title_kz: string;
  fields: PassportField[];
  blocks: PassportBlock[];
}

export interface PassportForm {
  kato: number;
  name_ru: string;
  name_kz: string;
  level: number;
  year: number;
  sections: PassportSection[];
}

export interface SaveFormRequest {
  kato: number;
  year: number;
  /** Только изменённые показатели. */
  scalars: Record<string, unknown>;
  /** Только изменённые блоки — каждый целиком. */
  blocks: {
    table: string;
    discriminator_value: string | null;
    rows: Record<string, unknown>[];
  }[];
}

/** Причина, по которой значение не записано, — готовым текстом ru/kz. */
export interface SaveIssue {
  key: string;
  code: string;
  message_ru: string;
  message_kz: string;
}

export interface SaveFormResult {
  saved_fields: number;
  saved_blocks: number;
  ignored: string[];
  /** Значения не прошли проверку — сохранение откатывается ЦЕЛИКОМ. */
  errors?: SaveIssue[];
  /**
   * Показатели на источнике (US-39 FR-11): не записаны, остальное сохранено.
   * Это состояние тумблера, а не ошибка формата (`code: "source_locked"`).
   */
  rejected?: SaveIssue[];
}

export const getPassportForm = async (
  kato: number,
  year: number,
): Promise<PassportForm> => {
  const response = await api.get<PassportForm>('/supervisor/passport-form', {
    params: { kato, year },
  });
  return response.data;
};

export const savePassportForm = async (
  body: SaveFormRequest,
): Promise<SaveFormResult> => {
  const response = await api.post<SaveFormResult>(
    '/supervisor/passport-form',
    body,
  );
  return response.data;
};

/**
 * Режим ввода показателя: конкретный источник (`bns`, `snp_monitoring`,
 * `treasury`), «Ручной ввод» (`manual`) либо прежнее двоичное «оставить
 * источник» (`source`) — им пользуется фолбэк для бэкенда до US-39.
 */
export type SourceMode = string;

/**
 * Запомнить выбор источника по показателю или блоку.
 *
 * Отдельный запрос, а не часть «Сохранить»: выбор определяет, что оператор может
 * править ПРЯМО СЕЙЧАС, и ждать кнопки не может. Ключ — тот же, что форма
 * прислала в GET (`field.key` либо `block.id`), гранулярность на сервере —
 * показатель × КАТО × год: раньше выбор жил в `localStorage` и был общим на все
 * территории.
 */
export const setSourceMode = async (
  kato: number,
  year: number,
  key: string,
  mode: SourceMode,
): Promise<void> => {
  await api.put('/supervisor/passport-form/source-mode', {
    kato,
    year,
    key,
    mode,
  });
};

const asObj = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};

/**
 * Ошибка, на которую сервер прислал готовый текст на ru и kz.
 *
 * Бэкенд отвечает так на всё, где виноват вход оператора: не тот формат файла
 * шаблона, не изображение, слишком большой файл, негодная рамка кадрирования.
 * Такие сообщения нужно показывать как есть — они точнее любого нашего
 * «не удалось загрузить».
 */
export class ApiMessageError extends Error {
  constructor(
    readonly messageRu: string,
    readonly messageKz: string,
  ) {
    super(messageRu);
    this.name = 'ApiMessageError';
  }
}

/** Разобрать `detail: {message_ru, message_kz}` из ответа. `null` — не наш случай. */
const asMessageError = (error: unknown): ApiMessageError | null => {
  const detail = asObj(asObj(asObj(asObj(error).response).data).detail);
  if (typeof detail.message_ru !== 'string') return null;
  return new ApiMessageError(
    detail.message_ru,
    String(detail.message_kz ?? ''),
  );
};

// ------------------------------------------------------- фото акима (FR-4.7)

/** Рамка кадрирования в пикселях ИСХОДНОГО изображения (после EXIF-поворота). */
export interface PhotoCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AkimPhotoResult {
  /** Новое значение `general.akim_image_id`. */
  image_id: string;
  /** Прямая ссылка на блоб; `null` у mock-хранилища — тогда см. `akimPhotoUrl`. */
  image_url: string | null;
  file_id: number;
  /** Пропорция, к которой сервер приводит фото: по ней рисуется рамка. */
  aspect: [number, number];
  message_ru: string;
  message_kz: string;
}

/**
 * Запасная ссылка на фото по id блоба, когда сервер её не дал (mock-хранилище
 * в тестах и дев-стенде). Публичный эндпоинт без токена — годится прямо в
 * `<img src>`: у тега нет наших axios-интерцепторов.
 *
 * ВНИМАНИЕ: здесь нет `region`, поэтому под united-бэкендом такой URL отвечает
 * 401 (`deps._asset_region_id`) — это и было DP-461. Боевая ссылка приходит с
 * сервера: `PassportField.image_url` / `AkimPhotoResult.image_url`.
 */
export const akimPhotoUrl = (imageId: string): string =>
  `/api/public/image/load?imageId=${encodeURIComponent(imageId)}`;

/**
 * Загрузить фото акима территории, опционально с рамкой кадрирования.
 *
 * Файл уходит как есть, а рамка — числами: так сервер режет ОРИГИНАЛ и сам
 * приводит результат к единому размеру. Если бы кадрировал браузер, картинка
 * пережималась бы дважды и качество зависело бы от устройства оператора.
 */
export const uploadAkimPhoto = async (
  kato: number,
  year: number,
  file: File,
  crop?: PhotoCrop,
): Promise<AkimPhotoResult> => {
  const body = new FormData();
  body.append('file', file);
  const params: Record<string, number> = { kato, year };
  if (crop !== undefined) {
    params.crop_x = Math.round(crop.x);
    params.crop_y = Math.round(crop.y);
    params.crop_w = Math.round(crop.width);
    params.crop_h = Math.round(crop.height);
  }
  try {
    const response = await api.post<AkimPhotoResult>(
      '/supervisor/passport-form/akim-photo',
      body,
      { params },
    );
    return response.data;
  } catch (error) {
    // 400/413 с ru/kz-сообщением (не изображение, велик файл, негодная рамка) —
    // показываем его текстом, а не «что-то пошло не так».
    const known = asMessageError(error);
    if (known !== null) throw known;
    throw error;
  }
};

// ---------------------------------------------- Excel-шаблон ввода данных

/**
 * Массовый ввод через Excel — вторая дорожка к тем же данным и тому же реестру.
 * Форма удобна для точечной правки, шаблон — когда заполняют территорию сразу
 * за несколько лет или собирают данные офлайн: в шаблоне семь колонок годов.
 */

export interface TemplatePreview {
  /** Отчётный год шаблона — центр окна колонок годов. */
  year: number | null;
  /** Годы, за которые в файле есть хоть одно значение. */
  years: number[];
  /** Все годы колонок шаблона (E:K), заполненные и пустые. */
  template_years: number[];
  region_mismatch: boolean;
  recognized_katos_total: number;
  matched_columns: string[];
  unknown_columns: string[];
  foreign_katos: number[];
  rejected_cells: {
    sheet: string;
    row: number;
    kato: number;
    key: string;
    reason: string;
  }[];
  /** Блок заменяется целиком — и отдельно за каждый год, а не «за территорию». */
  overwrite_warnings: {
    block: string;
    kato: number;
    year: number;
    incoming_rows: number;
  }[];
  message_ru: string;
  message_kz: string;
}

export interface TemplateApplyResult {
  applied: { scalars: number; blocks: number; years: number[] };
  skipped: { empty_blocks: number; errors: number };
  foreign_excluded: number;
  /**
   * Ячейки показателей, которые за этот год на источнике (US-39 FR-5c.4):
   * не записаны, остальное применено. До US-39 поля нет.
   */
  rejected_by_source?: {
    kato: number;
    year: number;
    key: string;
    code: string;
    message_ru: string;
    message_kz: string;
  }[];
  message_ru: string;
  message_kz: string;
}

/** Ошибка формата шаблона — бэкенд отвечает 400 с ru/kz-сообщением. */
export class TemplateFormatError extends ApiMessageError {
  constructor(messageRu: string, messageKz: string) {
    super(messageRu, messageKz);
    this.name = 'TemplateFormatError';
  }
}

const asFormatError = (error: unknown): TemplateFormatError | null => {
  const body = asObj(asObj(asObj(error).response as unknown).data as unknown);
  if (body.error !== 'bad_format' && body.error !== 'forbidden') return null;
  return new TemplateFormatError(
    String(body.message_ru ?? ''),
    String(body.message_kz ?? ''),
  );
};

/**
 * Скачать шаблон ВЫБРАННОЙ территории, уже заполненный её текущими данными.
 *
 * Ровно один КАТО — тот, что открыт в кабинете. Уровень территории роли не
 * играет: область, район, округ и село выгружаются одинаково. Многолистовые
 * книги (район со всеми округами и селами) остались за офлайн-скриптом на
 * бэкенде — оператору кабинета они только мешали бы искать свой лист.
 */
export const downloadTemplate = async (
  kato: number,
  year: number,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await api.get<Blob>(
    '/supervisor/passport-template/download',
    {
      params: { kato, year },
      responseType: 'blob',
    },
  );
  // Имя файла бэкенд отдаёт в RFC-5987 (`filename*=UTF-8''…`) — иначе кириллица
  // в браузере превратится в кракозябры.
  const disposition = String(
    asObj(response.headers)['content-disposition'] ?? '',
  );
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const filename = encoded
    ? decodeURIComponent(encoded[1])
    : 'passport_template.xlsx';
  return { blob: response.data, filename };
};

const postTemplate = async <T>(path: string, file: File): Promise<T> => {
  const body = new FormData();
  body.append('file', file);
  try {
    const response = await api.post<T>(path, body);
    return response.data;
  } catch (error) {
    const formatError = asFormatError(error);
    if (formatError !== null) throw formatError;
    throw error;
  }
};

export const previewTemplate = (file: File): Promise<TemplatePreview> =>
  postTemplate<TemplatePreview>('/supervisor/passport-template/preview', file);

export const applyTemplate = (file: File): Promise<TemplateApplyResult> =>
  postTemplate<TemplateApplyResult>(
    '/supervisor/passport-template/apply',
    file,
  );
